import { ApplicationRef, Component, EventEmitter, HostListener, Injector, Input, Output, QueryList, ViewChildren } from '@angular/core';
import { ILocationService } from "angular";
import { LogBookRootScopeService } from "app/shared/model/app.model";
import { ChoiceListSelectorComponent } from 'ng/src/app/choice-lists/choice-list-selector.component';
import { ChoiceList } from "ng/src/app/choice-lists/shared/choice-list.model";
import { OptionType } from 'ng/src/app/core/Option';
import { ResultType } from 'ng/src/app/core/Result';
import { Utils } from "ng/src/app/core/utils";
import { BaseComponent } from "ng/src/app/shared/base.component";
import { SelectorItem } from "ng/src/app/shared/item-selectors/item-selector.model";
import { QuerySelectorComponent } from 'ng/src/app/shared/item-selectors/query-selector/query-selector.component';
import { Nullable, Nullish } from "ng/src/app/shared/model/app.model";
import { addBlankSignatureListItem, createSegmentEditorFormGroup, SegmentEditorForm } from "ng/src/app/templates/segment-editor/segment-editor-form";
import { SegmentEditorService, TemplateSegmentEditorType } from "ng/src/app/templates/segment-editor/segment-editor.service";
import { Template, TemplateSegment, TemplateSegmentTypeId, TemplateType, TemplateTypeId, TypedTemplateSegment } from "ng/src/app/templates/shared/template.model";
import { TemplateService } from "ng/src/app/templates/shared/template.service";
import { LogUser, LogUserGroup } from "ng/src/app/users/shared/user.model";
import { UserService } from "ng/src/app/users/shared/user.service";
import { Result } from 'postcss';
import { ConfirmationService } from "primeng/api";
import { filter, takeUntil, tap } from "rxjs/operators";

@Component({
	selector: 'lb-segment-editor[template]',
	templateUrl: 'segment-editor.component.html',
	styleUrls: ['segment-editor.component.scss'],
	providers: [SegmentEditorService, ConfirmationService]
})
export class SegmentEditorComponent extends BaseComponent {
	@ViewChildren(ChoiceListSelectorComponent) querySelectorComponents!: QueryList<QuerySelectorComponent<any, any>>;
	
	@Input()
	set segment(val) {
		this.protectSegmentNavigation(() => {
			this._segment = val;
			this.setup();
		});
	}
	
	get segment() {
		return this._segment;
	}
	
	@Input()
	set template(val: Template) {
		this._template = val;
	}
	
	@Output()
	onSave = new EventEmitter<TemplateSegment>();
	
	get templateType(): TemplateTypeId {
		return this._template.templateType.id;
	}
	
	get logId() {
		return this._template.logId;
	}
	
	constructor(
		private templateService: TemplateService,
		private segmentEditorService: SegmentEditorService,
		private confirmationService: ConfirmationService,
		private userService: UserService,
		injector: Injector
	) {
		super(injector);
		window.addEventListener('beforeunload', (ev) => {
			if (this.formGroup?.dirty) ev.preventDefault();
		});
		
		LogBookRootScopeService.observeEvent(
			this.injector.get('$rootScope') as LogBookRootScopeService,
			'$locationChangeStart'
		)
			.pipe(
				takeUntil(this.onDestroy$),
				filter(() => !!this.formGroup?.dirty)
			)
			.subscribe(([event, next]) => {
				LogBookRootScopeService.stopRouteChange(event);
				this.protectSegmentNavigation(() => {
					delete this.formGroup;
					(this.injector.get('$location') as ILocationService).url(next);
				});
			});
	}
	
	readonly warningDialogKey = Utils.generateUniqueId();
	readonly SEGMENT_TYPES = TemplateSegmentTypeId;
	readonly TEMPLATE_TYPES = TemplateTypeId;
	
	readonly textStyleOptions: SelectorItem<NonNullable<TemplateSegment['textOption']>>[] = [
		SelectorItem.with('PLAIN_TEXT_ONLY', 'Plain Text Only'),
		SelectorItem.with('ALLOW_RICH_TEXT', 'Allow Rich Text'),
		SelectorItem.with('DEFAULT_TO_RICH_TEXT', 'Default Rich Text')
	];
	
	readonly textAlignmentOptions: SelectorItem<NonNullable<TemplateSegment['textAlignment']>>[] = [
		SelectorItem.with('LEFT', 'Left'),
		SelectorItem.with('RIGHT', 'Right'),
		SelectorItem.with('CENTER', 'Center')
	];
	
	readonly levelOptions: SelectorItem<1 | 2 | 3 | 4 | 5>[] = [
		SelectorItem.with(1, '1'),
		SelectorItem.with(2, '2'),
		SelectorItem.with(3, '3'),
		SelectorItem.with(4, '4'),
		SelectorItem.with(5, '5')
	];
	
	formGroup?: SegmentEditorForm;
	logUsers?: LogUser[];
	logUserGroups?: LogUserGroup[]
	
	private _segment: Nullish<TemplateSegmentEditorType>;
	private _template!: Template;
	private _segmentNavigationAccepted = false;
	
	@HostListener('keydown', ['$event'])
	onReturnKey(event: KeyboardEvent) {
		OptionType.nonNull(event.target)
			.filter((t) => {
				return event.key == 'Enter' && (event.metaKey || event.ctrlKey);
			})
			.whenSome(() => {
				event.preventDefault();
				event.stopPropagation();
				this.save();
			});
	}
	
	@HostListener('keydown.escape', ['$event'])
	onEscapeKey(event: KeyboardEvent) {
		event.preventDefault();
		event.stopPropagation();
		
		this.protectSegmentNavigation(() => {
			this.segment = undefined;
		})
	}
	
	protectSegmentNavigation(proceed: () => void, reject?: () => void) {
		const focusedElement = document.activeElement;
		if (this.formGroup?.dirty && !this._segmentNavigationAccepted) {
			this.confirmationService.confirm({
				key: this.warningDialogKey,
				header: 'Unsaved Changes',
				message: 'You may lose unsaved changes. Are you sure you want to leave?',
				accept: () => {
					this._segmentNavigationAccepted = true;
					this.protectSegmentNavigation(() => proceed())
				},
				reject:	() => {
					setTimeout(() => {
						if (focusedElement instanceof HTMLElement) {
							focusedElement.focus();
						}
					});
					reject?.();
				},
				acceptLabel: 'Proceed',
				rejectLabel: 'Cancel',
				acceptButtonStyleClass: 'p-button-danger',
				rejectButtonStyleClass: 'p-button-secondary',
				defaultFocus: 'reject'
			})
			return false;
		}
		
		proceed();
		this._segmentNavigationAccepted = false;
		
		return true;
	}
	
	hasProperties() {
		const propertyKeys: (keyof TemplateSegment)[] = [
			'key', 'calculate', 'required', 'variable'
		]
		return propertyKeys.some(key => !!this.formGroup?.get([key]))
	}
	
	getControl<K extends keyof TemplateSegment>(key: K) {
		const control = this.formGroup?.get([key]);
		return control as Nullable<SegmentEditorForm['controls'][K]>;
	}
	
	
	addTextListControl() {
		const control = this.getControl('textList');
		if (!control) return;
		addBlankSignatureListItem(control);
	}
	
	removeTextListControl(idx: number) {
		const arrayControl = this.getControl('textList');
		if (!arrayControl) return;
		arrayControl.removeAt(idx);
	}
	
	getSelectedList(): Nullish<ChoiceList> {
		const control = this.getControl('choiceList');
		if (!control) return;
		return control.value;
	}
	
	setControlState(key: keyof TemplateSegment, enabled: boolean) {
		this.getControl(key)?.[enabled ? 'enable' : 'disable']();
	}
	
	setControlValue<K extends keyof TemplateSegment>(key: K, value: Nullable<TemplateSegment[K]>) {
		this.getControl(key)?.setValue(value as never);
	}
	
	save() {
		if (!this.segment || !this.formGroup) return;
		this.segmentEditorService.save(this.segment, this.formGroup)
			.pipe(tap(() => this.formGroup?.markAsPristine()))
			.subscribe(res => this.onSave.emit(res));
	}
	
	private setup() {
		delete this.formGroup;
		if (!this.segment) return;
		this.formGroup = createSegmentEditorFormGroup(this.templateType, this.segment);
	}
}